import React, { useState, useEffect } from 'react';
import { useFetchWithAuth } from '../../hooks/useFetchWithAuth';

function ActiveSessionsCard() {
  const { fetchWithAuth, API_BASE } = useFetchWithAuth();
  const [sessions, setSessions] = useState([]);
  const [sessionCount, setSessionCount] = useState(0);
  const [maxSessions, setMaxSessions] = useState(5);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [showLogoutAllConfirm, setShowLogoutAllConfirm] = useState(false);

  const fetchSessions = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/auth/sessions`);
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
        setSessionCount(data.count || 0);
        setMaxSessions(data.max_sessions || 5);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSessions(); }, []);

  const handleRevoke = async (deviceId) => {
    setRevoking(deviceId);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/auth/sessions/${deviceId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchSessions();
      } else {
        alert('Failed to revoke session. Please try again.');
      }
    } catch (err) {
      alert('Error revoking session.');
    } finally {
      setRevoking(null);
    }
  };

  const handleLogoutAllOthers = async () => {
    setShowLogoutAllConfirm(false);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/auth/sessions/others`, {
        method: 'DELETE'
      });
      if (response.ok) {
        const data = await response.json();
        fetchSessions();
      }
    } catch (err) {
      alert('Error revoking other sessions.');
    }
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'ios' || deviceType === 'android') return '\uD83D\uDCF1';
    return '\uD83D\uDDA5\uFE0F';
  };

  const getTimeAgo = (isoString) => {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Active now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const otherSessions = sessions.filter(s => !s.is_current);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Active Sessions ({sessionCount} of {maxSessions})</h3>
      </div>
      <div className="p-5">
        {loading ? (
          <p className="asm-loadingText">Loading sessions...</p>
        ) : sessions.length === 0 ? (
          <p className="asm-emptyText">No active sessions found.</p>
        ) : (
          <>
            <div className="asm-sessionList">
              {sessions.map((s) => (
                <div key={s.device_id} className={`asm-sessionItem ${s.is_current ? 'asm-sessionItem--current' : ''}`}>
                  <div className="asm-sessionIcon">{getDeviceIcon(s.device_type)}</div>
                  <div className="asm-sessionInfo">
                    <div className="asm-sessionName">
                      {s.device_name}
                      {s.is_current && <span className="asm-currentBadge">This device</span>}
                    </div>
                    <div className="asm-sessionMeta">
                      {s.ip_address && <span>IP: {s.ip_address}</span>}
                      {s.ip_address && s.last_accessed && <span> · </span>}
                      {s.last_accessed && <span>{getTimeAgo(s.last_accessed)}</span>}
                    </div>
                  </div>
                  {!s.is_current && (
                    <button
                      className="btn btn-ghost asm-revokeBtn"
                      onClick={() => handleRevoke(s.device_id)}
                      disabled={revoking === s.device_id}
                    >
                      {revoking === s.device_id ? 'Revoking...' : 'Revoke'}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {otherSessions.length > 0 && (
              <div className="asm-logoutAllRow">
                {showLogoutAllConfirm ? (
                  <div className="asm-confirmRow">
                    <span>Logout all other devices?</span>
                    <button className="btn btn-secondary asm-confirmBtn" onClick={handleLogoutAllOthers}>Yes, Logout All</button>
                    <button className="btn btn-ghost asm-cancelBtn" onClick={() => setShowLogoutAllConfirm(false)}>Cancel</button>
                  </div>
                ) : (
                  <button
                    className="btn btn-secondary asm-logoutAllBtn"
                    onClick={() => setShowLogoutAllConfirm(true)}
                  >
                    Logout All Other Devices
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ActiveSessionsCard;
